import React, { useMemo, useState } from 'react';

import { dashboardVesselScheduleApis } from '@apis/next/vesselScheduleApis';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { VesselScheduleAddItemsDialog } from '@components/admin/widgets/VesselScheduleAddItemsDialog';
import { VesselScheduleDisplayEditRow } from '@components/admin/widgets/VesselScheduleDisplayEditRow';
import { Button } from '@components/Button';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { TextInput } from '@components/NextForms/TextInput';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { QueryContainer } from '@components/QueryContainer';
import { Table, cellFactory } from '@components/Table/Table';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { useSearch } from '@hooks/next/useSearch';
import { useAgency } from '@hooks/queries/dashboard/useAgency';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useDialogKey } from '@hooks/useDialogKey';
import { useFormUtils } from '@hooks/useFormUtils';
import { VesselScheduleHelper, VesselScheduleSearchQuery } from '@models/next/VesselSchedule';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { DEFAULT_PAGE_SIZE, formatDate } from '@utils/types';
import { useChecklist } from '@utils/useChecklist';
import { useEventCreator } from '@utils/useEventCreator';
import { pick, zip } from 'remeda';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: '本船スケジュール',
    addVessel: '新規追加',
    exportCSV: 'Export CSV',
    from: 'から',
    to: 'まで',
    exportSubject: '本船スケジュール',
    tagSearchInputPlaceholder: 'VESSEL、VOYAGE、POLやPODなどの検索条件を入力してください。',
    errorVesselSchedulesNotSelected: '本船スケジュールを複数選択して一括削除できます。',
    alertDeleteManyVesselScheduleItems: (n: number) =>
      `${n}項の本船スケジュールを削除しますか？削除した項目は戻すことができません。`,
    errorCannotDeleteVesselSchedulesDueTo: (
      vesselSchedules: {
        name: string;
        reason: string[];
      }[],
    ) => (
      <>
        下記の本船スケジュールは削除できません：
        <br />
        {vesselSchedules.map((item, i) => (
          <React.Fragment key={i}>
            {item.name}: {item.reason}
            <br />
          </React.Fragment>
        ))}
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '1rem',
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr 30px) 30px repeat(2, 1fr 30px)',
    rowGap: 20,
    columnGap: 8,
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: theme.palette.grey[50],
    padding: '1rem 1rem',
  },
  formContainer: {
    ...common.flexColumn,
    marginLeft: 20,
    marginTop: 28,
    marginBottom: 28,
    rowGap: '1.5rem',
  },
  inputContainer: {
    ...common.flexInline,
  },
  startContainer: {
    ...common.flexInline,
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  input: {
    width: 140,
  },
  joinLabel: {
    ...common.middleText,
    color: theme.palette.grey[700],
    margin: '0 0.5rem',
    paddingTop: 6,
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    paddingTop: 5,
  },
}));

const VesselScheduleSearchForm: React.FC = () => {
  const { search, updateSearch } = useSearch(VesselScheduleSearchQuery);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(VesselScheduleSearchQuery, {
    initialOutput: search,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const controller = useMemo(
    () => new TagSearchInputMobxZodControl(pick(fields, ['vessel', 'voyage', 'pol', 'pod']), form),
    [fields],
  );

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={strings.tagSearchInputPlaceholder}
        styles={{
          wrapper: {
            gridColumn: '1 / -1',
          },
        }}
      />

      <TextInput
        field={fields.etd_after}
        inputProps={{ placeholder: getFieldLabel(fields.etd_after) }}
        withErrorMessages={false}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.etd_before}
        inputProps={{ placeholder: getFieldLabel(fields.etd_before) }}
        withErrorMessages={false}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <div>{/* gap */}</div>

      <TextInput
        field={fields.eta_after}
        inputProps={{ placeholder: getFieldLabel(fields.eta_after) }}
        withErrorMessages={false}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.eta_before}
        inputProps={{ placeholder: getFieldLabel(fields.eta_before) }}
        withErrorMessages={false}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <div className={classes.buttonContainer}>
        <Button
          color="primary"
          shape="round"
          text={formStrings.filter}
          type="submit"
          size="small"
        />
        <Button
          color="primary"
          shape="round"
          text={formStrings.reset}
          type="submit"
          size="small"
          onClick={() => {
            form.root.setOutput(empty);
          }}
        />
      </div>
    </form>
  );
};

export const VesselSchedule: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(VesselScheduleSearchQuery);

  const {
    search: { action },
  } = useSearch(
    z
      .object({
        action: z.enum(['create']),
      })
      .partial(),
  );

  const vesselSchedulesQuery = dashboardVesselScheduleApis.useGet('/dashboard/vessel-schedule', {
    queries: {
      ...search,
      page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
    },
  });

  const userQuery = useUser();

  const agencyQuery = useAgency(userQuery.data?.agency_id!, {
    enabled: !!userQuery.data?.agency_id,
  });

  const vesselScheduleItems = vesselSchedulesQuery.data?.results ?? [];

  const checklist = useChecklist(
    [],
    vesselScheduleItems.map(({ id }) => id),
  );

  const editingList = useChecklist(
    [],
    vesselScheduleItems.map(({ id }) => id),
  );

  const deleteVesselSchedules = dashboardVesselScheduleApis.useDelete(
    '/dashboard/vessel-schedule',
    {
      queries: { id: checklist.checklist },
    },
    {
      onSuccess: () => vesselSchedulesQuery.invalidate(),
    },
  );

  const { renderSpinnerModal, renderAlertDialog } = useFormUtils({
    mutations: [deleteVesselSchedules],
  });

  const { confirm, renderDialog } = useConfirmDialog();

  const [addItemsOpen, setAddItemsOpen] = useState(action === 'create' ? true : false);
  const addItemsKey = useDialogKey(addItemsOpen);
  const [exportCsvOpen, setExportCsvOpen] = useState(false);

  const createRowOnCheckChange = useEventCreator((id: number) => () => checklist.toggle(id));
  const createRowOnEditingChange = useEventCreator(
    (id: number) => (editing) => editingList.setChecked(id, editing),
  );

  const renderTable = () => {
    const checkableHeaderColumn = cellFactory.createCheckable(
      checklist.allCheckedAndNotEmpty,
      checklist.toggleCheckAll,
    );

    return (
      <Table>
        <Table.Head>
          <Table.Row
            columns={[
              checkableHeaderColumn,
              cellFactory.create(vesselScheduleStrings.no),
              cellFactory.create(vesselScheduleStrings.vessel),
              cellFactory.create(vesselScheduleStrings.callsign),
              cellFactory.create(vesselScheduleStrings.voyage),
              cellFactory.create(vesselScheduleStrings.pol),
              cellFactory.create(vesselScheduleStrings.pod),
              cellFactory.create(vesselScheduleStrings.etd),
              cellFactory.create(vesselScheduleStrings.eta),
              cellFactory.create(vesselScheduleStrings.atd),
              cellFactory.create(vesselScheduleStrings.ata),
              cellFactory.create(''),
            ]}
          />
        </Table.Head>
        <Table.Body>
          {vesselScheduleItems.map((item, index) => (
            <VesselScheduleDisplayEditRow
              index={index + 1 + ((search.page ?? 1) - 1) * (search.page_size ?? 50)}
              key={item.id}
              item={item}
              checked={checklist.checked(item.id)}
              onCheckedChange={createRowOnCheckChange(item.id)}
              editing={editingList.checked(item.id)}
              onEditingChange={createRowOnEditingChange(item.id)}
            />
          ))}
        </Table.Body>
      </Table>
    );
  };

  return (
    <Layout
      title={strings.title}
      loading={userQuery.isLoading || (agencyQuery.isLoading && agencyQuery.isFetching)}
      errors={[userQuery.error, agencyQuery.error]}
    >
      <div className={classes.container}>
        {renderSpinnerModal()}
        {renderAlertDialog()}
        {renderDialog()}
        <VesselScheduleAddItemsDialog
          key={addItemsKey}
          open={addItemsOpen}
          onClose={() => setAddItemsOpen(false)}
        />
        <ExportCSVDialog
          open={exportCsvOpen}
          onClose={() => setExportCsvOpen(false)}
          subject={strings.exportSubject}
          baseUrl="/dashboard/vessel-schedule/"
          query={search}
          selectedIds={checklist.checklist}
        />
        <SectionTitle
          actions={
            <div className={classes.buttonContainer}>
              <Button
                color="primary"
                text={formStrings.delete}
                shape="round"
                style={{ width: 70 }}
                onClick={async () => {
                  if (checklist.checklist.length === 0) {
                    await confirm({
                      message: strings.errorVesselSchedulesNotSelected,
                    });
                  } else {
                    const deletingItems = vesselScheduleItems.filter((item) =>
                      checklist.checked(item.id),
                    );

                    const canDeleteItems = deletingItems.map((item) =>
                      VesselScheduleHelper.canWriteVesselSchedule(
                        item,
                        userQuery.data!,
                        agencyQuery.data,
                      ),
                    );

                    if (canDeleteItems.some((c) => c._tag === 'Left')) {
                      await confirm({
                        message: strings.errorCannotDeleteVesselSchedulesDueTo(
                          zip(deletingItems, canDeleteItems)
                            .filter(([_item, canDeleteItem]) => canDeleteItem._tag === 'Left')
                            .map(([item, canDeleteItem]) => ({
                              name: `${item.vessel} ${item.pol}/${item.pod} ${formatDate(
                                item.etd,
                              )}`,
                              reason:
                                canDeleteItem._tag === 'Left' ? canDeleteItem.left.messages : [],
                            })),
                        ),
                      });
                    } else if (
                      (await confirm({
                        message: strings.alertDeleteManyVesselScheduleItems(
                          checklist.checklist.length,
                        ),
                      })) === 'confirmed'
                    ) {
                      await deleteVesselSchedules.mutateAsync(undefined);
                      checklist.setChecklist([]);
                    }
                  }
                }}
              />

              <Button
                onClick={() => setAddItemsOpen(true)}
                color="primary"
                text={strings.addVessel}
                shape="round"
              />
              <Button
                onClick={() => setExportCsvOpen(true)}
                color="primary"
                text={strings.exportCSV}
                shape="round"
              />
            </div>
          }
        >
          {strings.title}
        </SectionTitle>

        <VesselScheduleSearchForm />

        <PageSizeForm />

        <div style={{ height: 16 }} />

        <QueryContainer queries={[vesselSchedulesQuery]}>{renderTable()}</QueryContainer>
        <div className={classes.paginationContainer}>
          <PaginationWithSearchQuery query={vesselSchedulesQuery} />
        </div>
      </div>
    </Layout>
  );
};
